export type FixSource = 'watch' | 'phone' | 'manual'

export interface PositionFix {
  latitude: number
  longitude: number
  accuracyMetres: number
  capturedAt: string
  source: FixSource
}

export interface ShotMark {
  eventId: string
  holeNumber: number
  club: string
  position: PositionFix
  recordedAt: string
  penalty?: boolean
}

export type FairwayResult = 'hit' | 'left' | 'right' | 'other'
export interface HoleResult { score?: number; putts?: number; chips?: number; fairway?: FairwayResult; greenInRegulation?: boolean }

export interface LocalRoundSession {
  id: string
  courseId: string
  courseName: string
  teeName: string
  currentHole: number
  startedAt: string
  updatedAt: string
  shots: ShotMark[]
  holes: Record<number, HoleResult>
  pendingEventIds: string[]
  serverVersion?: number
}

export interface ShotDistance {
  eventId: string
  metres?: number
  reason?: string
}

const radians = (degrees: number) => degrees * Math.PI / 180

export function metresBetween(a: PositionFix, b: PositionFix) {
  const radius = 6_371_000
  const dLat = radians(b.latitude - a.latitude)
  const dLon = radians(b.longitude - a.longitude)
  const lat1 = radians(a.latitude)
  const lat2 = radians(b.latitude)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
  return 2 * radius * Math.asin(Math.sqrt(h))
}

export function positionProblem(position: PositionFix, now = new Date()): string | undefined {
  if (!Number.isFinite(position.latitude) || position.latitude < -90 || position.latitude > 90 ||
      !Number.isFinite(position.longitude) || position.longitude < -180 || position.longitude > 180) return 'Invalid coordinates'
  if (!Number.isFinite(position.accuracyMetres) || position.accuracyMetres < 0) return 'Missing GPS accuracy'
  if (position.accuracyMetres > 35) return `GPS accuracy is only ±${Math.round(position.accuracyMetres)} m`
  const age = Math.abs(now.getTime() - new Date(position.capturedAt).getTime())
  if (!Number.isFinite(age) || age > 90_000) return 'GPS fix is stale'
  return undefined
}

export function shotDistances(session: LocalRoundSession, now = new Date()): ShotDistance[] {
  return session.shots.map((shot, index) => {
    const next = session.shots.slice(index + 1).find(candidate => candidate.holeNumber === shot.holeNumber)
    if (!next) return { eventId: shot.eventId, reason: 'Waiting for the next ball-position mark' }
    const issue = positionProblem(shot.position, now) ?? positionProblem(next.position, now)
    if (issue) return { eventId: shot.eventId, reason: issue }
    return { eventId: shot.eventId, metres: Math.round(metresBetween(shot.position, next.position)) }
  })
}

export function addShot(session: LocalRoundSession, shot: ShotMark): LocalRoundSession {
  if (session.shots.some(existing => existing.eventId === shot.eventId)) return session
  return {
    ...session,
    currentHole: shot.holeNumber,
    updatedAt: shot.recordedAt,
    shots: [...session.shots, shot],
    pendingEventIds: [...session.pendingEventIds, shot.eventId],
  }
}

export function undoLastShot(session: LocalRoundSession): LocalRoundSession {
  const removed = session.shots[session.shots.length - 1]
  if (!removed) return session
  return {
    ...session,
    shots: session.shots.slice(0, -1),
    pendingEventIds: session.pendingEventIds.filter(id => id !== removed.eventId),
    updatedAt: new Date().toISOString(),
  }
}

export function saveLocalRound(session: LocalRoundSession) {
  localStorage.setItem(`golf-round:${session.id}`, JSON.stringify(session))
  localStorage.setItem('golf-round:active', session.id)
}

export function loadActiveRound(): LocalRoundSession | undefined {
  const id = localStorage.getItem('golf-round:active')
  if (!id) return undefined
  const value = localStorage.getItem(`golf-round:${id}`)
  return value ? JSON.parse(value) as LocalRoundSession : undefined
}

export async function syncPendingRound(
  session: LocalRoundSession,
  deviceId: string,
  send: (expectedVersion: number, events: {eventId:string;deviceId:string;type:string;occurredAt:string;payload:Record<string,unknown>}[]) => Promise<{version:number;rebased:boolean;acknowledgedEventIds:string[]}>,
) {
  const pending = new Set(session.pendingEventIds)
  const events = session.shots.filter(shot => pending.has(shot.eventId)).map(shot => ({
    eventId: shot.eventId,
    deviceId,
    type: 'shot-mark',
    occurredAt: shot.recordedAt,
    payload: { holeNumber: shot.holeNumber, club: shot.club, position: shot.position, penalty: shot.penalty ?? false },
  }))
  if (!events.length) return {session, rebased:false}
  const result = await send(session.serverVersion ?? 0, events)
  const acknowledged = new Set(result.acknowledgedEventIds)
  return { session: {...session, serverVersion:result.version, pendingEventIds:session.pendingEventIds.filter(id=>!acknowledged.has(id))}, rebased:result.rebased }
}

export interface AuthoritativeRoundEvent { eventId:string; type:string; payloadJson:string; occurredAt:string }

export function reconcileAuthoritativeRound(session:LocalRoundSession, events:AuthoritativeRoundEvent[]):LocalRoundSession {
  const ordered=[...events].sort((a,b)=>a.occurredAt.localeCompare(b.occurredAt)||a.eventId.localeCompare(b.eventId))
  const shots=new Map<string,ShotMark>()
  const holes:Record<number,HoleResult>={}
  for(const event of ordered){
    const payload=JSON.parse(event.payloadJson) as Record<string,unknown>
    if(event.type==='shot-mark') shots.set(event.eventId,{eventId:event.eventId,holeNumber:Number(payload.holeNumber),club:String(payload.club),position:payload.position as PositionFix,penalty:Boolean(payload.penalty),recordedAt:event.occurredAt})
    if(event.type==='shot-undone') shots.delete(String(payload.targetEventId))
    if(event.type==='shot-corrected') { const target=shots.get(String(payload.targetEventId)); if(target) shots.set(target.eventId,{...target,...(payload.club?{club:String(payload.club)}:{})}) }
    if(event.type==='hole-result-set'||event.type==='hole-finished') holes[Number(payload.holeNumber)]={score:payload.score===undefined?undefined:Number(payload.score),putts:payload.putts===undefined?undefined:Number(payload.putts),...(payload.chips===undefined?{}:{chips:Number(payload.chips)}),...(typeof payload.fairway==='string'?{fairway:payload.fairway as FairwayResult}:{}),...(typeof payload.greenInRegulation==='boolean'?{greenInRegulation:payload.greenInRegulation}:{})}
    if(event.type==='hole-finished'&&payload.position) shots.set(`terminal:${event.eventId}`,{eventId:`terminal:${event.eventId}`,holeNumber:Number(payload.holeNumber),club:'Endpoint',position:payload.position as PositionFix,recordedAt:event.occurredAt})
  }
  const pending=new Set(session.pendingEventIds)
  const localPending=session.shots.filter(shot=>pending.has(shot.eventId)&&!shots.has(shot.eventId))
  return {...session,shots:[...shots.values(),...localPending],holes:{...session.holes,...holes}}
}
